import { useState, useEffect } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem
} from './ui/dropdown-menu';
import { fetchAvailableGenres, fetchAvailableTropes, Genre, Trope } from '../services/cmsService';

interface FiltersDropdownProps {
  selectedGenre: string;
  selectedSource: string;
  selectedStatus: string;
  onGenreChange: (genre: string) => void;
  onSourceChange: (source: string) => void;
  onStatusChange: (status: string) => void;
}


const STATUS_OPTIONS = [
  { label: 'Selected', value: 'true' },
  { label: 'Not Selected', value: 'false' }
];

export function FiltersDropdown({ 
  selectedGenre, 
  selectedSource, 
  selectedStatus,
  onGenreChange,
  onSourceChange,
  onStatusChange
}: FiltersDropdownProps) {
  const [open, setOpen] = useState(false);
  const [availableGenres, setAvailableGenres] = useState<Genre[]>([]);
  const [availableTropes, setAvailableTropes] = useState<Trope[]>([]);
  const [genresLoading, setGenresLoading] = useState(true);

  // Fetch dynamic genres from CMS API with fallback
  useEffect(() => {
    const fetchData = async () => {
      setGenresLoading(true);
      try {
        const genres = await fetchAvailableGenres();
        const tropes = await fetchAvailableTropes();
        
        // Use API data if available, otherwise use hardcoded fallback
        setAvailableGenres(genres || [
          { id: '1', name: 'Action', createdAt: '', updatedAt: '' },
          { id: '2', name: 'Comedy', createdAt: '', updatedAt: '' },
          { id: '3', name: 'Drama', createdAt: '', updatedAt: '' },
          { id: '4', name: 'Fantasy', createdAt: '', updatedAt: '' },
          { id: '5', name: 'Horror', createdAt: '', updatedAt: '' },
          { id: '6', name: 'Romance', createdAt: '', updatedAt: '' },
          { id: '7', name: 'Sci-Fi', createdAt: '', updatedAt: '' },
          { id: '8', name: 'Thriller', createdAt: '', updatedAt: '' },
        ]);
        
        setAvailableTropes(tropes || [
          { id: '1', name: 'Hero\'s Journey', createdAt: '', updatedAt: '' },
          { id: '2', name: 'Love Triangle', createdAt: '', updatedAt: '' },
          { id: '3', name: 'Chosen One', createdAt: '', updatedAt: '' },
          { id: '4', name: 'Enemies to Lovers', createdAt: '', updatedAt: '' },
          { id: '5', name: 'Coming of Age', createdAt: '', updatedAt: '' },
          { id: '6', name: 'Redemption Arc', createdAt: '', updatedAt: '' },
        ]);
      } catch (error) {
        console.error('Error fetching CMS data:', error);
        // Fallback to hardcoded data
        setAvailableGenres([
          { id: '1', name: 'Action', createdAt: '', updatedAt: '' },
          { id: '2', name: 'Comedy', createdAt: '', updatedAt: '' },
          { id: '3', name: 'Drama', createdAt: '', updatedAt: '' },
          { id: '4', name: 'Fantasy', createdAt: '', updatedAt: '' },
          { id: '5', name: 'Horror', createdAt: '', updatedAt: '' },
          { id: '6', name: 'Romance', createdAt: '', updatedAt: '' },
        ]);
      }
      setGenresLoading(false);
    };
    
    fetchData();
  }, []);

  const activeFiltersCount = [selectedGenre, selectedSource, selectedStatus].filter(Boolean).length;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="relative">
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFiltersCount > 0 && (
            <span className="ml-2 bg-primary text-primary-foreground rounded-full text-xs px-2 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Genre Filter */}
        <DropdownMenuLabel>Genre</DropdownMenuLabel>
        <div className="max-h-32 overflow-y-auto">
          <DropdownMenuCheckboxItem
            checked={selectedGenre === ''}
            onCheckedChange={(checked) => checked && onGenreChange('')}
          >
            All Genres
          </DropdownMenuCheckboxItem>
          {genresLoading ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">Loading genres...</div>
          ) : (
            availableGenres.map((genre) => (
              <DropdownMenuCheckboxItem
                key={genre.id}
                checked={selectedGenre === genre.name}
                onCheckedChange={(checked) => onGenreChange(checked ? genre.name : '')}
              >
                {genre.name}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Source Filter */}
        <DropdownMenuLabel>Source</DropdownMenuLabel>
        <div className="px-2 py-1">
          <Input
            placeholder="Type source name..."
            value={selectedSource}
            onChange={(e) => onSourceChange(e.target.value)}
            className="h-8 text-sm"
          />
          {selectedSource && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSourceChange('')}
              className="h-6 px-2 mt-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </Button>
          )}
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Status Filter */}
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={selectedStatus === ''}
          onCheckedChange={(checked) => checked && onStatusChange('')}
        >
          All Status
        </DropdownMenuCheckboxItem>
        {STATUS_OPTIONS.map((status) => (
          <DropdownMenuCheckboxItem
            key={status.value}
            checked={selectedStatus === status.value}
            onCheckedChange={(checked) => onStatusChange(checked ? status.value : '')}
          >
            {status.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}